<template>
  <div
    v-if="loaded || loading"
    class="mt-15 w-100"
    :class="{ gw_loading: loading }"
  >
    <div
      v-if="loading"
      class="f-column"
      style="gap: 0.5rem"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="gw_box gw_loading_text"
      >
        loading...
      </div>
    </div>
    <div
      v-else-if="status === 429"
      class="f-column py-8"
    >
      <IconAndText
        style="--icon-color: var(--clr-yellow)"
        icon="mdi-information-outline"
        :title="$t('fairplay.lookup_limit_exceeded')"
        :subtitle="$t('fairplay.lookup_limit_exceeded_subtitle', { num: 100 })"
      />
    </div>
    <div
      v-else-if="error"
      class="f-column py-8"
    >
      <IconAndText
        style="--icon-color: var(--clr-error)"
        icon="mdi-alert-circle-outline"
        :title="$t('messages.oops_error')"
        :subtitle="$t('messages.opps_error_sub')"
      />
    </div>
    <div v-else-if="games.length">
      <div class="font-clash-semibold text-big f-center uppercase">
        {{ $t('fairplay.spot_found') }}
      </div>
      <div class="text-small clr-grey f-center px-4 text-center">
        <div class="mdi mdi-information mr-1 mb-auto" />
        <div v-html="$t('fairplay.picker_info', { minutes: 5 })"></div>
      </div>

      <div style="max-width: 100%">
        <div class="tbl f-column mt-10 no-shrink">
          <div
            v-for="(blocks, idx) in rows"
            :key="idx"
            class="tbl_row"
          >
            <div
              v-for="(item, iIdx) in blocks"
              :key="`${item.id}_${item.value}_${idx}_${iIdx}`"
              class="tbl_row_item"
              :style="item.style"
            >
              <div
                class="csst tbl_row_item_title"
                @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
              >
                {{ item.title }}
                <div
                  class="csst_content text-no-wrap"
                  style="position: fixed"
                >
                  {{ item.tooltip }}
                </div>
              </div>
              <div class="tbl_row_item_value truncate">
                <div
                  v-if="item.id === 'id'"
                  class="gw_btn csst"
                  style="--clr-back: var(--clr-black-2); max-width: 100%"
                  @click="copyToClipboard(item)"
                  @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
                >
                  <div class="mdi mdi-content-copy mr-1 text-normal-large"></div>
                  <div class="truncate">
                    {{ item.value }}
                  </div>
                  <div
                    class="csst_content clr-white text-nowrap"
                    style="position: fixed"
                    :style="idCopied ? 'background: var(--clr-primary)' : ''"
                    v-html="
                      idCopied
                        ? $t('fairplay.id_copied_to_clipboard')
                        : $t('fairplay.copy_id_to_clipboard')
                    "
                  />
                </div>

                <div
                  v-else-if="item.id === 'board'"
                  class="d-flex no-events"
                >
                  <div
                    v-for="card in getCards(item.value)"
                    :key="card + 'streets'"
                    style="--height: 1.75em; --value-size-increase: 0.9"
                    :class="`poker-card poker-card_mini card_${card[1]}`"
                  >
                    <div class="card-value">
                      {{ card[0] }}
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="truncate csst"
                  @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
                >
                  {{ item.value }}
                  <div
                    class="csst_content clr-white"
                    style="position: fixed; max-width: 22rem; white-space: normal"
                  >
                    {{ item.valueTooltip || item.value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="mt-4 clr-grey text-center"
        v-html="
          $t('fairplay.spot_found_subtitle_tra01', {
            mailto: 'mailto:fairplay@gtowizard.com',
            email: 'fairplay@gtowizard.com',
          })
        "
      ></div>

      <div
        class="moved-left px-4 f-center pa-2 py-4 mt-8 clr-back-black-opacity b-radius_4 text-normal"
        style="background-color: rgba(0, 0, 0, 0.3); border: 1px solid rgb(14 94 126)"
      >
        <div
          class="mdi mdi-information crl-blue text-ultra mr-4"
          style="color: var(--clr-blue)"
        ></div>
        <div v-html="$t('fairplay.spot_found_subtitle_note')"></div>
      </div>

      <ExpandCard
        class="frpl_partners_contact mt-16 mx-auto"
        :hasScrollIntoView="false"
        :hasCloseIcon="false"
        :opened="false"
        style="
          --clr-back-title: transparent;
          --clr-back-title-active: transparent;
          --clr-back-content: transparent;
          --clr-back-title-hover: transparent;
          --title-padding: 0;
        "
      >
        <template #title="{ isOpen }">
          <div class="frpl_partners_contact_card">
            {{ $t('fairplay.contacts_for_sites') }}
            <div
              class="mdi icon_btn transition_03 expndcrd_title_icon_opener csst csst_nm mdi-chevron-down"
              :class="{
                rotated_180_before: isOpen,
              }"
            >
              <div
                class="csst_content clr-white"
                style="left: -0.25rem; transform: translate(-100%, 0)"
              >
                {{ isOpen ? $t('btn.collapse') : $t('btn.expand') }}
              </div>
            </div>
          </div>
        </template>
        <template #default>
          <div
            class="f-row f-wrap"
            style="gap: 2%"
          >
            <div
              v-for="(site, index) in partner_contacts"
              :key="index"
              class="gw_btn gw_btn_text gw_btn_nohover mt-3"
              style="--border: 1px solid #fdfdfd1a; gap: 2rem; justify-content: flex-start"
              :style="max768 ? 'width: 100%' : 'width: 48%'"
              :class="max768 ? 'px-2 py-4' : 'pa-5'"
            >
              <div
                class="d-flex"
                style="width: 10rem"
              >
                <img
                  class="img-logo mr-2"
                  :src="site.img"
                />
                <div class="text-normal-large weight_600 my-auto">
                  {{ site.name }}
                </div>
              </div>
              <div
                class="gw_btn gw_btn_text clr-primary text-medium csst ml-auto"
                :style="max540 ? 'width: 9rem' : 'width: 13rem'"
                style="line-height: 1.2"
                @click="copyPartnerContact(`${site.email}`)"
              >
                <span class="mdi mdi-content-copy clr-grey mr-1" />
                <span class="truncate mr-auto">{{ site.email }}</span>
                <div
                  class="csst_content text-nowrap clr-white"
                  :style="emailCopied === site.email ? 'background: var(--clr-primary)' : ''"
                  style="
                    position: absolute;
                    top: 0;
                    transform: translateY(-110%);
                    font-size: 0.75rem;
                  "
                  v-html="
                    emailCopied === site.email
                      ? $t('fairplay.email_copied_to_clipboard')
                      : $t('fairplay.copy_email_to_clipboard')
                  "
                ></div>
              </div>
            </div>
          </div>
        </template>
      </ExpandCard>
    </div>
    <div
      v-else
      class="f-column f-center"
    >
      <div class="font-clash-semibold text-big f-center uppercase">
        {{ $t('fairplay.spot_not_found') }}
      </div>
      <div class="clr-grey">
        {{ $t('fairplay.spot_not_found_subtitle') }}
      </div>
    </div>
  </div>
</template>

<script>
import ExpandCard from '@/components/Common/ExpandCard/ExpandCard'
import IconAndText from '@/components/Poker/Overlays/IconAndText'
import { formatISO } from 'date-fns'
import format_mix from '@/mixins/format_mix'
import float_mix from '@/mixins/float_mix'
import screen_mix from '@/mixins/screen_mix'
import { fairplaySitesOptions } from '@/helpers/constants'

export default {
  name: 'FairplayGames',
  components: {
    ExpandCard,
    IconAndText,
  },
  mixins: [format_mix, float_mix, screen_mix],
  props: {
    game: {
      default: () => ({ cards: [], time: null }),
    },
  },
  data() {
    return {
      idCopied: '',
      emailCopied: '',

      error: false,
      loading: false,
      loaded: false,
      status: 0,
      games: [],

      // -- dev only
      // loaded: true,
      // status: 200,
      // loading: true,
      // error: true,
      // games: [
      //   {
      //     id: 'ec9e8fd8-2128-4832-9a4e-962781e1c18e',
      //     created_at: '2023-10-04T00:52:40.602863+02:00',
      //     original_board: 'QsTs7h8h4h',
      //     actions: 'F-F-R2.5-F-F-C|X',
      //     // format: 'Cash',
      //     format: 'Custom',
      //     depth: '100.000',
      //     stacks: '100.000-100.000-100.000-100.000-100.000-100.000',
      //     pot: '5.50',
      //     ip_position: 'CO',
      //     oop_positions: ['CO', 'BB'],
      //   },
      //   {
      //     id: 'ec9dse8fd8-2128-4832-9a4e-962781e1c18e',
      //     created_at: '2023-10-04T14:52:40.602863+02:00',
      //     original_board: 'QsTs7h8h4h',
      //     actions: 'F-F-R2.5-F-F-C|X',
      //     // format: 'Cash',
      //     format: 'Custom',
      //     depth: '100.000',
      //     stacks: '100.000-100.000-100.000-100.000-100.000-100.000',
      //     pot: '5.50',
      //     ip_position: 'CO',
      //     oop_positions: ['CO', 'BB'],
      //   },
      //   {
      //     id: 'edc9e8fd8-2128-4832-9a4e-962781e1c18e',
      //     created_at: '2023-10-04T23:52:40.602863+02:00',
      //     original_board: 'QsTs7h8h4h',
      //     actions: 'F-F-R2.5-F-F-C|X',
      //     // format: 'Cash',
      //     format: 'Custom',
      //     depth: '100.000',
      //     stacks: '100.000-100.000-100.000-100.000-100.000-100.000',
      //     pot: '5.50',
      //     ip_position: 'CO',
      //     oop_positions: ['CO', 'BB'],
      //   },
      // ],
    }
  },
  methods: {
    copyToClipboard(item) {
      console.log('copyToClipboard', item)
      this.idCopied = item.value
      navigator.clipboard.writeText(item.value)
      setTimeout(() => {
        this.idCopied = ''
      }, 1500)
    },
    copyPartnerContact(email) {
      this.emailCopied = email
      navigator.clipboard.writeText(email)
      setTimeout(() => {
        this.emailCopied = ''
      }, 2000)
    },
    async checkGame({ cards, time }) {
      this.loading = true
      this.games = []
      this.error = null
      this.loaded = false

      const params = {
        board: cards.filter(card => !card.includes('E')).join(''),
        timestamp: formatISO(time),
      }

      const result = await this.$store.dispatch('api_call', {
        method: 'POST',
        endpoint: 'poker/fair-play/check/',
        data: params,
        hasCancel: false,
      })

      this.loading = false
      this.loaded = true

      this.status = result?.status
      this.error = !result || result?.error || !result?.response

      if (this.error) return
      this.games = result.response.data
    },
  },
  computed: {
    rows() {
      const res = this.games.map(i => {
        const aiFormat = i.format.toLowerCase() === 'custom'
        return [
          {
            id: 'id',
            title: this.$t('common.id'),
            tooltip: this.$t(`fairplay.spot_tooltips.id`),
            value: i.id,
            style: 'min-width: 8rem;',
          },
          {
            id: 'board',
            title: this.$t('common.board'),
            tooltip: this.$t(`fairplay.spot_tooltips.board`),
            value: i.original_board,
            style: 'min-width: 6.25rem;',
          },
          {
            id: 'date',
            title: this.$t('common.date'),
            tooltip: this.$t(`fairplay.spot_tooltips.date`),
            style: 'min-width: 9rem;',
            value: this.formatISODate(i.created_at, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
          },
          {
            id: 'time',
            title: this.$t('common.time'),
            tooltip: this.$t(`fairplay.spot_tooltips.time`),
            value: this.formatDate(i.created_at, 'HH:mm:ss'),
          },
          {
            id: 'format',
            title: this.$t('common.format'),
            tooltip: this.$t(`fairplay.spot_tooltips.format`),
            value: aiFormat ? 'GTO Wizard AI' : i.format,
            style: 'min-width: 8.25rem;',
          },
          {
            id: 'stacks',
            title: this.$t('common.stacks'),
            tooltip: aiFormat
              ? this.$t(`fairplay.spot_tooltips.stacks_ai`)
              : this.$t(`fairplay.spot_tooltips.stacks`),
            value: i.stacks
              .split('-')
              .map(x => this.numRoundTo5bb(Number(x)))
              .join(', '),
          },
          {
            id: 'pot',
            title: this.$t('common.pot'),
            tooltip: this.$t(`fairplay.spot_tooltips.pot`),
            value: `${this.numFixedUnit(Number(i.pot), 'bb')}bb`,
          },
          {
            id: 'oop',
            title: this.$t('common.oop'),
            tooltip: this.$t(`fairplay.spot_tooltips.oop`),
            value: aiFormat
              ? 'N/A'
              : i.oop_positions.map(pp => this.$t(`players.${pp}`)).join(', '),
            valueTooltip: aiFormat ? this.$t(`fairplay.spot_tooltips_custom`) : '',
          },
          {
            id: 'ip',
            title: this.$t('common.ip'),
            tooltip: this.$t(`fairplay.spot_tooltips.ip`),
            value: aiFormat ? 'N/A' : this.$t(`players.${i.ip_position}`),
            valueTooltip: aiFormat ? this.$t(`fairplay.spot_tooltips_custom`) : '',
          },
        ]
      })
      // console.log(res)
      return res
    },
    cardSize() {
      return this.max768 ? 'smallest' : 'small'
    },
    partner_contacts() {
      const contacts = fairplaySitesOptions()
      return contacts.map(x => ({
        ...x,
        img: new URL(`/src/assets/playhouses/${x.img}.webp`, import.meta.url).href,
      }))
    },
  },
  watch: {
    game({ cards, time }) {
      if (cards && time) {
        this.checkGame({ cards, time })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.frpl_partners_contact_card {
  display: flex;
  justify-content: center;
  align-items: center;
  width: clamp(1px, 32rem, 100vw);
  margin: auto;
  border-radius: 4px;
  background: var(--clr-black-1);
  padding: 0.5rem 1rem;
  height: 2.5rem;
}
h2 {
  font-weight: 600;
  font-size: 1.75rem;
  margin-bottom: 1.75rem;
}
@media (max-width: 540px) {
  .spot-row {
    padding: 0.2rem 0.1rem;
  }
}

// ------------------------------
.tbl {
  width: 100%;
  display: flex;
  flex-direction: column;
  &_row {
    display: flex;
    min-width: 0;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-weight: 500;

    .tbl_row_item_title {
      display: none;
    }
    &:first-child {
      .tbl_row_item_title {
        display: flex;
      }
    }
    &_item {
      display: flex;
      flex: 1;
      min-width: 0;
      flex-direction: column;
      --padding-unit: 0.5rem;
      &:last-child {
        .tbl_row_item_value {
          border-radius: 0 4px 4px 0;
        }
      }
      &:first-child {
        .tbl_row_item_value {
          border-radius: 4px 0 0 4px;
        }
      }
      &_title {
        font-size: 1rem;
        font-weight: 700;
        margin-right: auto;
        padding: 0 var(--padding-unit) 0.5rem var(--padding-unit);
      }
      &_value {
        min-width: 0;
        display: flex;
        align-items: center;
        background: var(--clr-black-1);
        padding: 0.5rem var(--padding-unit);
        min-height: 3rem;
      }
    }
  }
}

@media (max-width: 1023px) {
  .tbl {
    flex-direction: column;
    gap: 1rem;
    &_row {
      border: solid 1px var(--clr-black-2);
      padding: 0.5rem;
      .tbl_row_item_title {
        display: flex;
      }
      flex-direction: column;
      &_item {
        &_title {
          padding: 0.5rem var(--padding-unit) 0.5rem var(--padding-unit);
        }
        &_value {
          border-radius: 4px;
        }
      }
    }
  }
}
</style>
